import { Button, Tooltip } from '@skupreme/shared/ui';
import { useAuth } from '@contexts/auth';
import { useSidebar } from '@contexts/sidebar';
import { isActionDisabled, isActionHidden } from '@hooks/url-permission';
import {
  CommonConstant,
  sheetTypes,
  tooltipTexts,
} from '@utility/constants/common';
import OrganizationsService from '@utility/services/organization';
import { ToastMessage } from '@utility/toast';
import { useState } from 'react';

type SheetsContext = {
  data?: { general_sheets?: { type: string; sheet_id?: string }[] };
};

/*
 * Bulk upload: the organisation's catalog Google Sheet when it has one
 * (refreshed, then opened), the CSV import otherwise. Starter plans get neither.
 */
export function BulkUploadButton({ onUpload }: { onUpload: () => void }) {
  const { data } = (useSidebar() ?? {}) as SheetsContext;
  const { user, isSuperAdmin, isStarterPlan } = useAuth();
  const [opening, setOpening] = useState(false);

  const sheetId = data?.general_sheets?.find(
    x => x.type === 'catalog',
  )?.sheet_id;
  const permission = user?.permissions?.Sheets?.modules?.Catalog;
  const allowed =
    (!isActionDisabled(permission) && !isActionHidden(permission)) ||
    isSuperAdmin;

  if (
    isStarterPlan ||
    (sheetId && isActionHidden(permission) && !isSuperAdmin)
  ) {
    return null;
  }

  if (!sheetId) {
    return (
      <Button variant="pill" size="md" onPress={onUpload}>
        Bulk upload
      </Button>
    );
  }

  const openSheet = async () => {
    setOpening(true);
    try {
      await OrganizationsService.refreshGoogleSheets(sheetTypes.catalog);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setOpening(false);
      window.open(
        `${CommonConstant.googleSheetUrl}/${sheetId}`,
        '_blank',
        'noreferrer',
      );
    }
  };

  return (
    <Tooltip content={tooltipTexts.navBarSheet} className="sk:max-w-125">
      <Button
        variant="pill"
        size="md"
        isDisabled={!allowed}
        isPending={opening}
        onPress={openSheet}
      >
        Bulk upload
      </Button>
    </Tooltip>
  );
}
